/**
 * Reserves the `Object.prototype` keys the EqualWeb overlay would otherwise define as
 * read-only (ENG-4334); left to EqualWeb they break the resident login (ENG-4510).
 *
 * EqualWeb's loader mirrors builtins onto the intrinsic prototypes under an `IND` prefix,
 * sourcing the values from a pristine iframe so page-level monkey-patching cannot break
 * it. On `Object.prototype` it mirrors three, each `writable: false, configurable: false`.
 *
 * That is not inert. Polymer's `PropertiesChanged`, bundled inside
 * `@descope/web-components-ui` and therefore present in every resident login, snapshots
 * native property names by walking `HTMLElement.prototype` up to `Object.prototype` and
 * assigning each own name onto a plain object. A plain object inherits those read-only
 * definitions, so the assignment throws in strict mode: the module owning
 * `vaadin-text-field` never initializes, the Descope flow renders an empty shadow root,
 * and residents get a spinner where the login form should be.
 *
 * Reserving the keys first is a no-op for EqualWeb. Its mirror loop skips any key the page
 * already owns (`!target.hasOwnProperty(key)`) and swallows failures in a try/catch, and
 * the values reserved here are the same builtins it would have installed. The only
 * difference is that ours stay writable, so the snapshot assignment succeeds.
 *
 * A version bump is not an alternative: 5.0.13, 5.1.1, 5.1.2 and 5.2.0 (the newest build)
 * all pollute identically, and the one build that does not, `core/1.0.0`, is from March
 * 2019. Ordering is the entire mechanism, so call this before appending the loader script
 * — afterwards the keys are already non-configurable and nothing can repair them.
 */

import { LogWarning } from 'utils/loggingHelpers';

const MIRRORED_OBJECT_BUILTINS: Readonly<Record<string, unknown>> = {
  INDpropertyIsEnumerable: Object.prototype.propertyIsEnumerable,
  INDhasOwnProperty: Object.prototype.hasOwnProperty,
  INDisPrototypeOf: Object.prototype.isPrototypeOf,
};

export const EQUAL_WEB_RESERVED_OBJECT_KEYS: readonly string[] = Object.keys(MIRRORED_OBJECT_BUILTINS);

export const reserveEqualWebPrototypeKeys = (): void => {
  Object.entries(MIRRORED_OBJECT_BUILTINS).forEach(([key, value]) => {
    const existing = Object.getOwnPropertyDescriptor(Object.prototype, key);
    if (existing) {
      if (existing.writable === false) {
        LogWarning('EqualWeb prototype key already read-only; Descope login will fail', { key });
      }
      return;
    }

    // Extending it is the fix, not an oversight: EqualWeb extends it regardless, and only
    // the page can get there first.
    // eslint-disable-next-line no-extend-native
    Object.defineProperty(Object.prototype, key, {
      value,
      writable: true,
      enumerable: false,
      configurable: true,
    });
  });
};
